/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Localizer from '../../helpers/localization';
import { BroadcastStatus, WebAppHelpersLocationStatus, getBroadcastStatus, getLocationStatus } from '../../helpers/ui/location/Format';
import { type NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import './StatusPicto.css';

// Picto status is refreshed every 30 seconds
const REFRESH_TIMEOUT: number = 30_000;

type DefaultProps = {
  +className?: string,
  +forcedText?: string,
  +onClick?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => Promise<void> | void,
};

export type StatusPictoPropType = {
  ...DefaultProps,
  +item: NETGEM_API_V8_FEED_ITEM,
};

const StatusPicto = ({ className = '', forcedText, item, onClick }: StatusPictoPropType): React.Node => {
  const refreshTimer = useRef<TimeoutID | null>(null);
  const [broadcastStatus, setBroadcastStatus] = useState<BroadcastStatus>(BroadcastStatus.Unknown);

  const updateBroadcastStatus = useCallback(() => {
    setBroadcastStatus(getBroadcastStatus(item));
  }, [item]);

  const resetRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const startRefreshTimer = useCallback(() => {
    resetRefreshTimer();

    if (broadcastStatus === BroadcastStatus.Past) {
      // A past item will never change, no need to check again
      return;
    }

    refreshTimer.current = setTimeout(updateBroadcastStatus, REFRESH_TIMEOUT);
  }, [broadcastStatus, resetRefreshTimer, updateBroadcastStatus]);

  useEffect(() => {
    startRefreshTimer();
  }, [broadcastStatus, startRefreshTimer]);

  // Only contains a cleanup function
  useEffect(() => () => resetRefreshTimer(), [resetRefreshTimer]);

  useEffect(() => {
    updateBroadcastStatus();
  }, [item, updateBroadcastStatus]);

  let status = getLocationStatus(item);
  if (!status || status === WebAppHelpersLocationStatus.Vod || status === WebAppHelpersLocationStatus.App || broadcastStatus === BroadcastStatus.Unknown) {
    return null;
  }

  if (broadcastStatus === BroadcastStatus.Live) {
    status = WebAppHelpersLocationStatus.Live;
  } else if (broadcastStatus === BroadcastStatus.Preview) {
    status = WebAppHelpersLocationStatus.Preview;
  }

  if (onClick) {
    // Render status as a button
    return (
      <button className={clsx('statusPicto clickable', status as string, className)} onClick={onClick} tabIndex={0} type='button'>
        {forcedText || Localizer.localize(`common.status_picto.${status as string}`)}
      </button>
    );
  }

  // Render status as a div
  return <div className={clsx('statusPicto', status as string, className)}>{forcedText || Localizer.localize(`common.status_picto.${status as string}`)}</div>;
};

export default StatusPicto;
